import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  VStack,
  Heading,
  Text,
  useToast,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  FormHelperText,
  SimpleGrid,
} from '@chakra-ui/react';
import { cricketModelService, NextBallOutcomeRequest, NextBallOutcomeResponse } from '../../services/cricketModelService';

export const NextBallOutcomeForm: React.FC = () => {
  const [innings, setInnings] = useState<number>(1);
  const [currentBall, setCurrentBall] = useState<number>(0);
  const [wkts, setWkts] = useState<number>(0);
  const [runs, setRuns] = useState<number>(0);
  const [target, setTarget] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [result, setResult] = useState<NextBallOutcomeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const request: NextBallOutcomeRequest = {
        innings,
        currentBall,
        wkts,
        runs,
        ...(target && target.trim() !== '' && { target: parseInt(target) }),
      };
      
      const response = await cricketModelService.getNextBallOutcomePercentages(request);
      setResult(response);
      
      toast({
        title: 'Success',
        description: 'Next ball outcome percentages calculated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      setError('Failed to calculate next ball outcome percentages. Please try again.');
      toast({
        title: 'Error',
        description: 'Failed to calculate next ball outcome percentages',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatOutcome = (outcome: string): string => {
    // Convert snake_case to Title Case with spaces
    return outcome
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getSortedOutcomes = (inning: string) => {
    if (!result || !result.value || !result.value[inning]) return [];
    
    const customOrder = ['R0', 'R1', 'R2', 'R3', 'R4', 'R5', 'R6', 'WICKET', 'WIDE'];
    
    return Object.entries(result.value[inning])
      .sort((a, b) => {
        const outcomeA = a[0].toUpperCase();
        const outcomeB = b[0].toUpperCase();
        
        const indexA = customOrder.indexOf(outcomeA);
        const indexB = customOrder.indexOf(outcomeB);
        
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }
        
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        
        return outcomeA.localeCompare(outcomeB);
      })
      .map(([outcome, percentage]) => ({
        outcome,
        percentage,
        formattedOutcome: formatOutcome(outcome)
      }));
  };

  return (
    <Box>
      <Heading size="md" mb={4}>Next Ball Outcome Percentages</Heading>
      <Text mb={4}>
        Calculate the percentages for possible outcomes of the next ball.
      </Text>
      
      <Box as="form" onSubmit={handleSubmit}>
        <VStack spacing={3} align="stretch">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Innings</FormLabel>
              <NumberInput min={0} max={2} value={innings} onChange={(_, value) => setInnings(value)}>
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <FormHelperText fontSize="xs">0 for prematch, 1 for first innings, 2 for second innings</FormHelperText>
            </FormControl>
            
            <FormControl isRequired>
              <FormLabel fontSize="sm">Current Ball</FormLabel>
              <NumberInput min={0} max={6} value={currentBall} onChange={(_, value) => setCurrentBall(value)}>
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <FormHelperText fontSize="xs">Number of balls bowled (0-6)</FormHelperText>
            </FormControl>
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Wickets</FormLabel>
              <NumberInput min={0} max={10} value={wkts} onChange={(_, value) => setWkts(value)}>
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>
            
            <FormControl isRequired>
              <FormLabel fontSize="sm">Runs</FormLabel>
              <NumberInput min={0} value={runs} onChange={(_, value) => setRuns(value)}>
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>
          </SimpleGrid>

          <FormControl>
            <FormLabel fontSize="sm">Target (Optional)</FormLabel>
            <NumberInput min={0} value={target} onChange={(valueString) => setTarget(valueString)}>
              <NumberInputField placeholder="Leave empty for innings 1" />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <FormHelperText fontSize="xs">Only required for second innings (innings 2)</FormHelperText>
          </FormControl>
          
          <Button type="submit" colorScheme="blue" isLoading={isLoading}>
            Calculate Outcome Percentages
          </Button>
        </VStack>
      </Box>
      
      {error && (
        <Text color="red.500" mt={4}>
          {error}
        </Text>
      )}
      
      {result && result.value && (
        <VStack spacing={3} mt={4} align="stretch">
          <Text fontSize="xs" color="gray.600" fontWeight="bold">Calculated in {result.durationMillis}ms</Text>
          
          {Object.keys(result.value).sort().map((inning) => (
            <Box key={inning} p={2} borderWidth={1} borderRadius="md">
              <Text fontSize="xs" fontWeight="bold" mb={2}>{inning}</Text>
              <SimpleGrid columns={{ base: 3, md: 6, lg: 9 }} spacing={1}>
                {getSortedOutcomes(inning).map(({ outcome, percentage, formattedOutcome }) => {
                  const isR4 = outcome.toUpperCase() === 'R4';
                  const isR6 = outcome.toUpperCase() === 'R6';
                  const labelColor = isR4 ? 'orange.600' : isR6 ? 'red.600' : 'gray.700';
                  const bgColor = isR4 ? 'orange.50' : isR6 ? 'red.50' : 'gray.50';
                  
                  return (
                    <Box key={outcome} p={1} bg={bgColor} borderRadius="sm">
                      <Text fontSize="sm" fontWeight="bold" color={labelColor}>{formattedOutcome}</Text>
                      <Text fontSize="sm" color="blue.600" fontWeight="bold">{(percentage * 100).toFixed(1)}%</Text>
                    </Box>
                  );
                })}
              </SimpleGrid>
            </Box>
          ))}
        </VStack>
      )}
    </Box>
  );
};
